// trailVisibilityBus — globale Map-Visibility-Maske.
//
// Wofür:
//   Auf der TrailsPage kann der User filtern (Near-Me, Difficulty). Die
//   gefilterte Liste soll er sich OPTIONAL auch auf der Karte limitieren
//   können — "zeig mir nur diese 5 Trails im Kartenbild" (User-Wunsch
//   Wave 14x).
//
// Design:
//   Ein Set<trailId> wird in einem Singleton gehalten.
//     - null   = "keine Filterung, alle Trails zeigen" (Default)
//     - Set<>  = "nur diese IDs sichtbar"
//   MapPage filtert `mapTrails` durch diese Maske und re-rendert, wenn sich
//   der Snapshot ändert. TrailsPage setzt / clearsed die Maske per Button.
//
// Warum kein context/location.state?
//   - MapPage ist dauerhaft gemountet (Tab-Wechsel → kein Re-Mount).
//   - Pattern matcht die anderen Buses (locateControlBus, compassBus,
//     mapIntentsBus) → konsistent, minimale Cognitive Load.

type Listener = (snapshot: ReadonlySet<string> | null) => void;

class TrailVisibilityBus {
  private current: ReadonlySet<string> | null = null;
  private readonly listeners = new Set<Listener>();

  /** Aktueller Snapshot — `null` wenn kein Filter aktiv ist. */
  get(): ReadonlySet<string> | null {
    return this.current;
  }

  /** Setzt die Visibility-Maske. `null` → Filter aus (alle Trails sichtbar). */
  set(ids: ReadonlySet<string> | null): void {
    // Referenz-Gleichheit früh abbrechen, damit Subscriber nicht unnötig
    // feuern, wenn der State semantisch unverändert bleibt.
    if (ids === this.current) return;
    if (ids && this.current && setsEqual(ids, this.current)) return;
    this.current = ids;
    for (const l of this.listeners) l(this.current);
  }

  /** Convenience: Filter komplett zurücksetzen. Äquivalent zu `set(null)`. */
  clear(): void {
    this.set(null);
  }

  /** Subscriber für React's useSyncExternalStore-Pattern. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

/** Kurz-Check auf Set-Gleichheit. Sets sind klein (≤ Trail-Count), daher
 *  trivial — kein Grund für Hash- oder Marker-Vergleich. */
function setsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export const trailVisibilityBus = new TrailVisibilityBus();
